/**
 * SidebarVisibilityContext
 *
 * Estado global que controla si la barra lateral está completamente oculta
 * (no solo colapsada a iconos). Distinto del estado `collapsed` del Sidebar:
 *
 * - `collapsed: true`  → sidebar visible en 64px (solo iconos, con handle para expandir)
 * - `hidden: true`     → sidebar NO se renderiza, main toma 100% del ancho
 *
 * Los dos flags son independientes: el usuario puede tener el sidebar
 * colapsado a iconos y luego ocultarlo del todo, o expandirlo y ocultarlo.
 *
 * El estado se persiste en `localStorage` con clave `sidebar.hidden` para
 * que la preferencia sobreviva a recargas. Escribimos con try/catch porque
 * Safari en modo privado y algunos navegadores embebidos tiran
 * SecurityError al hacer setItem.
 *
 * El botón para ocultar vive en la `FloatingControlBar` del WebRTCPage
 * (caso de uso principal: maximizar el área del video remoto). El botón
 * flotante para restaurar vive en `MainLayout` y aparece en el borde
 * izquierdo de cualquier página cuando el sidebar está oculto — así el
 * usuario nunca queda "atrapado" sin poder traerlo de vuelta.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

interface SidebarVisibilityContextValue {
  hidden: boolean;
  setHidden: (hidden: boolean) => void;
  toggle: () => void;
}

const SidebarVisibilityContext = createContext<SidebarVisibilityContextValue | undefined>(
  undefined
);

const SIDEBAR_HIDDEN_KEY = 'sidebar.hidden';

function readInitialHidden(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_HIDDEN_KEY) === 'true';
  } catch {
    return false;
  }
}

export function SidebarVisibilityProvider({ children }: { children: ReactNode }) {
  const [hidden, setHiddenState] = useState<boolean>(() => readInitialHidden());

  // Persist on change
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_HIDDEN_KEY, String(hidden));
    } catch {
      // Ignore quota / privacy errors (Safari private mode, etc.)
    }
  }, [hidden]);

  const setHidden = useCallback((next: boolean) => {
    setHiddenState(next);
  }, []);

  const toggle = useCallback(() => {
    setHiddenState((prev) => !prev);
  }, []);

  const value = useMemo<SidebarVisibilityContextValue>(
    () => ({ hidden, setHidden, toggle }),
    [hidden, setHidden, toggle]
  );

  return (
    <SidebarVisibilityContext.Provider value={value}>{children}</SidebarVisibilityContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarVisibility(): SidebarVisibilityContextValue {
  const ctx = useContext(SidebarVisibilityContext);
  if (!ctx) {
    throw new Error('useSidebarVisibility must be used within SidebarVisibilityProvider');
  }
  return ctx;
}

export default SidebarVisibilityContext;
